import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Modal, ModalDialogue, theme } from '@sg/garnish';
import { HStack } from '@sg/garnish';
import {
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { useLocalizationContext } from '@order/Localization';
import {
  telemetryProductFromInput,
  useTrackEventEffect,
} from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationNoDressingModal = (props: Props) => {
  const { actor } = props;

  const { t } = useLocalizationContext();

  // ─── Context ─────────────────────────────────────────────────────────

  const { product, shouldShowNoDressingModal } = useCustomizationContext({
    actor,
  });
  const { continueCustomization, endCustomization } = useCustomizationHelpers({
    actor,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'pdp.view.modal.no-dressing',
    payload: { selectedProduct: product ? telemetryProductFromInput(product) : undefined, }, // prettier-ignore
    skip: !shouldShowNoDressingModal,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      testID="pdp-modals.no-dressing"
      visible={shouldShowNoDressingModal}
      size="medium"
      accessibilityLabel={t('pdp.modifications.no-dressing-modal.heading')}
      headline={t('pdp.modifications.no-dressing-modal.heading')}
      body={t('pdp.modifications.no-dressing-modal.text')}
      onRequestClose={continueCustomization}
    >
      <SafeAreaView edges={['bottom']}>
        <Modal.Footer withoutTopBorder>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size="large"
              palette="secondary"
              onPress={continueCustomization}
              testID="pdp-modals.no-dressing.go-back"
            >
              {t('general.go-back')}
            </Button>

            <Button
              size="large"
              palette="primary"
              onPress={endCustomization}
              testID="pdp-modals.no-dressing.continue"
            >
              {t('general.continue')}
            </Button>
          </HStack>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
};
